import { Rng } from '../core/rng';
import { Bout, type BoutStats } from './Bout';
import { ROSTER } from './data';
import type { BoutOutcome, OpponentDef } from './types';

export interface InterludeOptions {
  /** Night number in this session (1 = the first shift change). */
  level: number;
  seed: number;
}

export interface BoutRecord {
  opponent: OpponentDef;
  outcome: BoutOutcome;
  flawless: boolean;
}

export interface InterludeResult {
  level: number;
  bouts: BoutRecord[];
  calmed: number;
  total: number;
  stats: BoutStats;
}

/** Customers calmed between shifts make the next shift's crowd this much more patient, each. */
export const CALM_PATIENCE_PER_CUSTOMER = 0.08;

/**
 * The shift change: three rowdy customers in a row — one easy, one medium,
 * one boss — chosen from the roster by the session seed.
 */
export class Interlude {
  readonly options: InterludeOptions;
  readonly opponents: OpponentDef[];
  readonly records: BoutRecord[] = [];
  private readonly rng: Rng;
  private superMeter = 0;
  private readonly totals: BoutStats = {
    hits: 0,
    parries: 0,
    supers: 0,
    chairCatches: 0,
    maxCombo: 0,
  };

  constructor(options: InterludeOptions) {
    this.options = options;
    this.rng = new Rng(options.seed);
    this.opponents = ([1, 2, 3] as const).map((tier) =>
      this.rng.pick(ROSTER.filter((o) => o.tier === tier)),
    );
  }

  get index(): number {
    return this.records.length;
  }

  get done(): boolean {
    return this.records.length >= this.opponents.length;
  }

  get current(): OpponentDef | undefined {
    return this.opponents[this.records.length];
  }

  createBout(): Bout {
    const opponent = this.current;
    if (!opponent) throw new Error('No opponents left in this interlude');
    return new Bout({
      opponent,
      level: this.options.level,
      seed: this.rng.int(0, 0x7fffffff),
      round: this.index + 1,
      totalRounds: this.opponents.length,
      superMeter: this.superMeter,
    });
  }

  finish(bout: Bout): void {
    if (!bout.outcome) throw new Error('Bout has not ended');
    this.records.push({
      opponent: bout.opponentDef,
      outcome: bout.outcome,
      flawless: bout.flawless && bout.outcome === 'calmed',
    });
    // The super meter carries between rounds — you earned it.
    this.superMeter = bout.superMeter;
    this.totals.hits += bout.stats.hits;
    this.totals.parries += bout.stats.parries;
    this.totals.supers += bout.stats.supers;
    this.totals.chairCatches += bout.stats.chairCatches;
    this.totals.maxCombo = Math.max(this.totals.maxCombo, bout.stats.maxCombo);
  }

  result(): InterludeResult {
    return {
      level: this.options.level,
      bouts: [...this.records],
      calmed: this.records.filter((r) => r.outcome === 'calmed').length,
      total: this.opponents.length,
      stats: { ...this.totals },
    };
  }
}
